import React from 'react';
import { BsX, BsEye, BsPause, BsPlay, BsTrash, BsPlusCircle } from 'react-icons/bs';

interface HelpDrawerComposicoesProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '20px' }}>
    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '6px' }}>
      {title}
    </h4>
    <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{children}</div>
  </div>
);

const ActionItem: React.FC<{ icon: React.ReactNode; label: string; description: string; bgColor: string; borderColor?: string; textColor?: string }> = ({ icon, label, description, bgColor, borderColor, textColor }) => (
  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
    <span style={{
      fontSize: '12px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      borderRadius: '3px',
      border: `1px solid ${borderColor || '#ccc'}`,
      backgroundColor: bgColor,
      color: textColor || 'black',
    }}>{icon}</span>
    <div>
      <strong>{label}</strong>
      <div style={{ color: '#666', fontSize: '12px' }}>{description}</div>
    </div>
  </div>
);

const DRAWER_WIDTH = 320;

const HelpDrawerComposicoes: React.FC<HelpDrawerComposicoesProps> = ({ isOpen, onClose, children }) => {
  return (
    <>
      <div style={{ marginRight: isOpen ? DRAWER_WIDTH : 0, transition: 'margin-right 0.3s ease' }}>
        {children}
      </div>

      <div style={{
        position: 'fixed',
        top: 0,
        right: isOpen ? 0 : -DRAWER_WIDTH,
        width: DRAWER_WIDTH,
        height: '100vh',
        backgroundColor: '#fff',
        borderLeft: '1px solid #e0e0e0',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
        transition: 'right 0.3s ease',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #2a2d3a 0%, #3c3f4c 100%)',
          color: '#fff',
        }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Ajuda — Composições</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}
          >
            <BsX />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', fontSize: '13px' }}>
          <Section title="Visão Geral">
            <p style={{ margin: 0 }}>
              Templates de composição definem as regras de formação de bancas: quantos membros de cada vínculo e papel são necessários. Cada template é vinculado a uma categoria de edital e tipo de banca.
            </p>
          </Section>

          <Section title="Filtros">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Busca:</strong> Nome ou descrição do template.</li>
              <li><strong>Categoria:</strong> Categoria do edital (Aluno Graduação, Servidor, etc.).</li>
              <li><strong>Tipo:</strong> Tipo de banca (Facial, Virtual, Recursal).</li>
              <li><strong>Status:</strong> Ativos, Inativos ou Todos.</li>
            </ul>
          </Section>

          <Section title="Tabela">
            <p style={{ margin: '0 0 8px 0' }}>
              Templates são agrupados por nome e categoria. Cada grupo pode ter variantes para diferentes tipos de banca.
            </p>
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Tipo:</strong> Facial, Virtual ou Recursal.</li>
              <li><strong>Vínculos:</strong> Regras de mínimo e máximo por vínculo (ex: Docente: 1-1).</li>
              <li><strong>Status:</strong> Ativo (verde) ou Inativo (cinza).</li>
            </ul>
          </Section>

          <Section title="Ações">
            <ActionItem icon={<BsEye />} label="Visualizar" description="Abre os detalhes completos do template." bgColor="#f8fafc" borderColor="#e2e8f0" />
            <ActionItem icon={<BsPause />} label="Desativar" description="Desativa o template. Não será usado na criação de novas bancas." bgColor="#fef3c7" borderColor="#fef3c7" textColor="#d97706" />
            <ActionItem icon={<BsPlay />} label="Ativar" description="Reativa um template desativado." bgColor="#dcfce7" borderColor="#dcfce7" textColor="#16a34a" />
            <ActionItem icon={<BsTrash />} label="Excluir" description="Remove o template permanentemente. Ação irreversível." bgColor="#fee2e2" borderColor="#fee2e2" textColor="#dc2626" />
            <ActionItem icon={<BsPlusCircle style={{ color: 'white' }} />} label="Novo Template" description="Cria um novo template de composição com regras de vínculos e papéis." bgColor="#2563eb" borderColor="#2563eb" textColor="white" />
          </Section>

          <Section title="Dicas">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li>Ao criar com tipo "Todos", o sistema gera automaticamente para Facial, Virtual e Recursal.</li>
              <li>Templates inativos não aparecem na consulta de disponibilidade.</li>
              <li>Cada processo usa o template ativo correspondente à sua categoria de edital.</li>
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
};

export default HelpDrawerComposicoes;
